import { DataTable, Column } from 'fluent-components';
import { Pagination } from 'fluent-components';
import { Status } from '@/types';
import { useNavigate } from 'react-router-dom';
import StatusCell from '@/pages/UserManagement/UserList/components/StatusCell';
import EscalationStatus from '../Supervisor/components/EscalationStatus';
import { QAAudit } from '@/types/doctorManagementTypes';
import Breadcrumb from '@/components/common/Breadcrumb';
import { useQuery } from '@apollo/client';
import { QA_AUDIT_QUERY } from '@/graphql/queries/record-management';
import { AuditEvent } from '@/types/recordManagement/audit';
import { toast } from '@/components/hooks/use-toast';
import { Loader } from 'lucide-react';
import { useEffect, useState } from 'react';
import { documentReferenceAPIs } from '@/lib/axios/apis/recordManagement/document';
import moment from 'moment';
import { newPermissionsFeatureCodes } from '@/constants';
import useUserInfo from '@/components/Authenticate/useUserInfo';

const columns: Column<QAAudit>[] = [
  {
    key: 'ticketNo',
    label: 'Ticket No.',
    type: 'text',
  },
  {
    key: 'escalatedBy',
    label: 'Escalated by',
    type: 'text',
  },
  {
    key: 'role',
    label: 'Role',
    type: 'text',
    render: () => {
      return (
        <div className="flex flex-row gap-2">
          <span className="bg-zinc-100 rounded-md px-2 py-1 text-sm">
            Reviewer
          </span>
          <span className="bg-zinc-100 rounded-md px-2 py-1">
            OCR Validator
          </span>
        </div>
      );
    },
  },
  {
    key: 'issue',
    label: 'Issue',
    type: 'text',
  },
  {
    key: 'dateTime',
    label: 'Date & Time',
    type: 'text',
  },
  {
    key: 'status',
    label: 'Status',
    type: 'text',
    render: (status: QAAudit['status']) => (
      <StatusCell status={status as Status} />
    ),
  },
];

interface QAAuditQueryResponse {
  AuditEventList: AuditEvent[];
}

export default function QAListPage() {
  const [summaryCount, setSummaryCount] = useState(0);
  const navigate = useNavigate();
  const { userPermissions } = useUserInfo();

  const handleRowClick = (escalation: QAAudit) => {
    const hasPermission = userPermissions?.[newPermissionsFeatureCodes.quality_check_conduct] === 'CHECK_RESULT_ALLOWED';
    if (hasPermission) {
      navigate(`/qa-audit-flow/${escalation.ticketNo}`, { state: escalation });
    } else {
      toast({
        title: 'Warning',
        description: `You don't have access!`,
        variant: 'destructive',
      });
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const page = Number(queryParams.get('page') || 1);
  const { data, error, loading } = useQuery<QAAuditQueryResponse>(
    QA_AUDIT_QUERY,
    {
      variables: {
        _count: 10,
        _offset: (page - 1) * 10,
      },
    }
  );
  useEffect(() => {
    documentReferenceAPIs.getQADocsSummaryCount().then((res) => {
      setSummaryCount(res?.data?.total || 0);
    });
  }, []);

  if (error) {
    toast({
      title: 'Error',
      description: error?.message,
      variant: 'destructive',
    });
  }

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    navigate({ search: params.toString() });
  };

  const handlePageSizeChange = (size: number) => {
    const params = new URLSearchParams(location.search);
    params.set('pageSize', size.toString());
    navigate({ search: params.toString() });
  };

  const getFullNameFromAuditEvent = (auditEvent: AuditEvent) => {
    const name = auditEvent.agent?.[0]?.who?.resource?.name?.[0] || {
      given: [''],
      family: '',
    };
    return `${name.given?.[0]} ${name.family}`;
  };

  const transformDoctorData = () => {
    if (!data || !data.AuditEventList.length) {
      return [];
    }
    return data.AuditEventList.map((auditEvent) => {
      return {
        dateTime: auditEvent?.period?.start ? moment(auditEvent?.period?.start).format('MMMM DD, YYYY, HH:mm A') : null,
        escalatedBy: getFullNameFromAuditEvent(auditEvent),
        issue: '',
        issueType: '',
        status: 'Active',
        role: '',
        ticketNo: auditEvent.id,
      };
    });
  };

  if (loading) {
    return <Loader className="fixed left-1/2 top-1/2 animate-spin h-10 w-10" />;
  }

  const QA_STATUS_DATA = [
    {
      label: 'Total',
      value: summaryCount,
      bgColor: 'bg-indigo-100',
      textColor: 'text-zinc-900',
    },
    {
      label: 'Checked',
      value: '03',
      bgColor: 'bg-emerald-100',
      textColor: 'text-teal-700',
    },
    {
      label: 'Pending',
      value: '07',
      bgColor: 'bg-rose-50',
      textColor: 'text-red-700',
    },
  ];

  return (
    <div
      className="overflow-y-scroll p-8 h-full"
      style={{ background: '#f8f8ff' }}
    >
      <Breadcrumb
        items={[
          { label: 'Dashboard', path: '/' },
          { label: 'QA Audit', path: '/qa-audit' },
        ]}
      />
      <EscalationStatus data={QA_STATUS_DATA} title="QA Audit" />
      <DataTable<QAAudit>
        data={transformDoctorData()}
        columns={columns}
        onRowClick={handleRowClick}
      />

      <div className="flex justify-end">
        <Pagination
          totalItems={summaryCount}
          currentPage={page || 1}
          pageSize={10}
          showItemsPerPageDropdown={false}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
}
